/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { createQuery } from '@/utils/createReactQuery';
import { type AxiosInstance } from 'axios';
import { type QueryClient } from '@tanstack/react-query';
import type { ReviewSummary } from '../interface/types';
import { isRetryableError } from '../utils/retryStrategies';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import type { Locale } from '@/types/i18n';
import environmentConfig from '@/config/environment';

const URL = `${requestBaseURLGCPExtOrInt}/sls/product/product-reviews-integration-bs/v1/review-summaries`;

type GetReviewSummaryOptions = {
  skuNumber: string;
  locale?: Locale;
};

const getReviewSummary = async (
  options: GetReviewSummaryOptions,
  axiosInstance?: AxiosInstance,
) => {
  const { skuNumber, locale = countryCodes.us } = options;
  const response = await getAxios(axiosInstance).get<ReviewSummary>(`${URL}/${skuNumber}`, {
    params: {
      locale: locale.replace('-', '_'),
    },
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });
  return response.data;
};

const { useData: useReviewSummaryData, prefetch: prefetchReviewSummaryData } = createQuery<
  ReviewSummary,
  GetReviewSummaryOptions
>('reviewSummary', async (options, axiosInstance) =>
  getReviewSummary({ ...options }, axiosInstance),
);

type ReviewSummaryOptions = GetReviewSummaryOptions & {
  enabled?: boolean;
  retry?: boolean | number | ((failureCount: number, error: Error) => boolean);
  retryDelay?: number | ((retryAttempt: number, error: unknown) => number);
};

export const useReviewSummary = (options: ReviewSummaryOptions) => {
  const { enabled = true, ...rest } = options;
  const locale = useLocale();
  return useReviewSummaryData({
    ...rest,
    locale,
    enabled,
    retry: isRetryableError,
    retryDelay: 1000,
  });
};

export const prefetchReviewSummary = (
  options: GetReviewSummaryOptions,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance,
) => {
  return prefetchReviewSummaryData(queryClient, options, axiosInstance);
};
